// path-matrix.ts — Pure function to compute wizard step path from intake answers.
// MANUS-WIZARD-V2 Task 2. No React imports. No side effects. No API calls.

export type ProjectType = 'saas' | 'internal' | 'api' | 'cli' | 'static' | 'custom';
export type DeployTarget = 'railway' | 'flyio' | 'render' | 'vps' | 'unsure';
export type DatabaseChoice = 'supabase' | 'own' | 'none';

export type WizardStep =
  | 'github'
  | 'railway'
  | 'deploy_config'   // generic for non-Railway targets
  | 'supabase'
  | 'mailgun'
  | 'netlify'
  | 'stripe'
  | 'deploy_verify';

export interface IntakeAnswers {
  projectType: ProjectType;
  deployTarget: DeployTarget;
  databaseChoice: DatabaseChoice;
  customDescription?: string;
  customServices?: WizardStep[];
}

/** Human-readable labels for each wizard step. */
export const STEP_LABELS: Record<WizardStep, string> = {
  github: 'GitHub',
  railway: 'Railway',
  deploy_config: 'Deploy Config',
  supabase: 'Supabase',
  mailgun: 'Mailgun',
  netlify: 'Netlify',
  stripe: 'Stripe',
  deploy_verify: 'Deploy & Verify',
};

/**
 * Compute the wizard step path from intake answers.
 *
 * Rules:
 * 1. GitHub is always first.
 * 2. Deploy & Verify is always last.
 * 3. Middle steps are determined by project type + deploy target + database choice.
 * 4. Non-Railway deploy targets use deploy_config instead of railway.
 *    "Unsure" skips the deploy step entirely.
 * 5. Own database omits supabase.
 * 6. Stripe only for SaaS.
 * 7. Mailgun only when there's a backend (not CLI, not Static).
 * 8. Netlify only when there's a frontend (not API-only, not CLI).
 */
export function computeWizardPath(answers: IntakeAnswers): WizardStep[] {
  const { projectType, deployTarget, databaseChoice } = answers;

  // Custom type with explicit service selection
  if (projectType === 'custom' && answers.customServices) {
    const middle = answers.customServices.filter(
      s => s !== 'github' && s !== 'deploy_verify',
    );
    return ['github', ...middle, 'deploy_verify'];
  }

  // CLI: just github + deploy_verify
  if (projectType === 'cli') {
    return ['github', 'deploy_verify'];
  }

  // Static: github + netlify + deploy_verify
  if (projectType === 'static') {
    return ['github', 'netlify', 'deploy_verify'];
  }

  // For saas, internal, api — build middle steps
  const middle: WizardStep[] = [];

  // Deploy step
  if (deployTarget === 'railway') {
    middle.push('railway');
  } else if (deployTarget !== 'unsure') {
    // flyio, render, vps → generic deploy config
    middle.push('deploy_config');
  }
  // "unsure" → no deploy step

  // Database step
  if (databaseChoice === 'supabase') {
    middle.push('supabase');
  }
  // "own" or "none" → no supabase step

  // Mailgun — only when there's a backend (saas, internal, api all have backends)
  middle.push('mailgun');

  // Netlify — only when there's a frontend (saas, internal have frontends; api does not)
  if (projectType === 'saas' || projectType === 'internal') {
    middle.push('netlify');
  }

  // Stripe — only for SaaS
  if (projectType === 'saas') {
    middle.push('stripe');
  }

  return ['github', ...middle, 'deploy_verify'];
}

/** Convenience: get the step count for a given set of answers. */
export function computeStepCount(answers: IntakeAnswers): number {
  return computeWizardPath(answers).length;
}

/** Convenience: get human-readable step names for a given set of answers. */
export function computeStepNames(answers: IntakeAnswers): string[] {
  return computeWizardPath(answers).map(s => STEP_LABELS[s]);
}

// ─── Inline assertions (verifiable by inspection) ─────────────────────

// computeWizardPath({ projectType: 'cli', deployTarget: 'unsure', databaseChoice: 'none' })
// → ['github', 'deploy_verify']

// computeWizardPath({ projectType: 'static', deployTarget: 'unsure', databaseChoice: 'none' })
// → ['github', 'netlify', 'deploy_verify']

// computeWizardPath({ projectType: 'saas', deployTarget: 'railway', databaseChoice: 'supabase' })
// → ['github', 'railway', 'supabase', 'mailgun', 'netlify', 'stripe', 'deploy_verify']

// computeWizardPath({ projectType: 'saas', deployTarget: 'flyio', databaseChoice: 'supabase' })
// → ['github', 'deploy_config', 'supabase', 'mailgun', 'netlify', 'stripe', 'deploy_verify']

// computeWizardPath({ projectType: 'saas', deployTarget: 'unsure', databaseChoice: 'supabase' })
// → ['github', 'supabase', 'mailgun', 'netlify', 'stripe', 'deploy_verify']

// computeWizardPath({ projectType: 'internal', deployTarget: 'railway', databaseChoice: 'supabase' })
// → ['github', 'railway', 'supabase', 'mailgun', 'netlify', 'deploy_verify']

// computeWizardPath({ projectType: 'internal', deployTarget: 'railway', databaseChoice: 'own' })
// → ['github', 'railway', 'mailgun', 'netlify', 'deploy_verify']

// computeWizardPath({ projectType: 'api', deployTarget: 'railway', databaseChoice: 'supabase' })
// → ['github', 'railway', 'supabase', 'mailgun', 'deploy_verify']

// computeWizardPath({ projectType: 'api', deployTarget: 'railway', databaseChoice: 'none' })
// → ['github', 'railway', 'mailgun', 'deploy_verify']

// computeWizardPath({ projectType: 'custom', deployTarget: 'railway', databaseChoice: 'none', customServices: ['railway', 'mailgun'] })
// → ['github', 'railway', 'mailgun', 'deploy_verify']
